import type { ReactElement } from "react"
import TextAtm from "@onestore/hel/dist/components/atoms/TextAtm"
import AlignContentOrg from "@onestore/hel/dist/components/organisms/AlignContentOrg"
import BreakpointVisibilityOrg from "@onestore/hel/dist/components/organisms/BreakpointVisibilityOrg"
import FlexContainerOrg from "@onestore/hel/dist/components/organisms/FlexContainerOrg"
import PushOrg from "@onestore/hel/dist/components/organisms/PushOrg"
import WidthOrg from "@onestore/hel/dist/components/organisms/WidthOrg"
import _t from "@core/i18n"
import type { PeriodInfo } from "@core/period-info"
import { CURRENCY_SEPARATOR } from "@core/pricing"
import type { HorizontalProductBox } from "@gatsby-plugin-generic-page/fragments/horizontalProductBox"
import { Price } from "@ionos-web-design-system/react"
import type { PostlineProps } from "@ionos-web-design-system/react/price"
import type { PriceInfo } from "~/fragments/priceInfo"
import usePriceConfigurationQuery from "~/hooks/usePriceConfigurationQuery"
import isEmpty from "~/lib/isEmpty"
import ButtonElement from "./ButtonElement"

type Props = {
  hasPromotion: boolean
  priceValue: string
  lowestPrice: string
  lowestPricePercent: string
  renewalPrice?: string
  renewalPriceText?: string
  currency: string
  priceTypeText: string
  buttonHref: string
  productData: HorizontalProductBox
  promotedHorizontalProductBox?: HorizontalProductBox[]
  priceInfo?: PriceInfo
  periodInfo?: PeriodInfo
  withoutButton?: boolean
}

const calculateDiscount = (
  currentPrice: string,
  renewalPrice?: string
): string | undefined => {
  if (!renewalPrice) return undefined

  const current = parseFloat(currentPrice.replace(",", "."))
  const renewal = parseFloat(renewalPrice.replace(",", "."))

  if (isNaN(current) || isNaN(renewal) || renewal === 0) return undefined

  const discount = parseInt(
    Math.round(100 - (100 * current) / renewal).toFixed(0),
    10
  )

  return `${discount}%`
}

export default function PriceCell({
  hasPromotion,
  priceValue,
  lowestPrice,
  lowestPricePercent: _lowestPricePercent,
  renewalPrice,
  renewalPriceText,
  currency,
  priceTypeText,
  buttonHref,
  productData,
  promotedHorizontalProductBox,
  priceInfo,
  periodInfo,
  withoutButton,
}: Props): ReactElement<Props> {
  const { showDetailedPrices } = usePriceConfigurationQuery()

  const discountPercent = calculateDiscount(priceValue, renewalPrice) || "0%"

  const postLines: PostlineProps[] = [
    {
      content: `${priceTypeText} ${_t(
        `periodName.priceMol.${periodInfo?.getPeriodType()}`,
        {
          smart_count: periodInfo?.getPeriodLength(),
        }
      )}`,
      weight: "black",
    },
  ]

  if (priceInfo?.hasRenewalPriceText) {
    const renewalText: PostlineProps = {
      content: `${_t("prices.renewalPrefix")} ${renewalPriceText}`,
      weight: "normal",
    }

    postLines.push(renewalText)
  }

  if (!isEmpty(priceInfo?.labels)) {
    const labels: PostlineProps[] = (priceInfo?.labels ?? []).map((label) => ({
      content: label.flatData.text,
      tooltip: label.flatData.tooltip,
      weight: "normal",
    }))

    postLines.push(...labels)
  }

  if (hasPromotion) {
    const omnibusText: PostlineProps = {
      content: `${_t("prices.lowestPriceWithType", { priceType: "" })} ${lowestPrice} ${currency}`,
      weight: "normal",
    }

    postLines.push(omnibusText)
  }

  const udsPrice = (
    <Price
      alignment="right"
      data={{
        main: {
          comma: CURRENCY_SEPARATOR,
          currency,
          currencySymbolPosition: "after",
          decimalPrice: priceValue.split(",")[1] || "00",
          integerPrice: priceValue.split(",")[0],
        },
        postlines: postLines,
        prelines: {
          primary: {
            discount:
              discountPercent !== "0%"
                ? `${hasPromotion ? _t("priceMol.discountLabel") : ""} -${discountPercent}`
                : undefined,
            strike: discountPercent !== "0%" ? renewalPrice : undefined,
          },
        },
      }}
      size="medium"
      variant="simple"
      emphasis={hasPromotion}
      data-test-id="product-box-uds-price"
      forcePostlineWeight
      className="leading-normal"
    />
  )

  const priceElement = (
    <PushOrg
      bottomSpace={{
        small: hasPromotion ? 0.5 : 2,
        medium: hasPromotion ? 0.5 : 2,
        large: hasPromotion ? 0.5 : 1,
      }}
    >
      <TextAtm
        color={hasPromotion ? "primary" : undefined}
        typography="big3"
        dataTestId="product-box-price-price"
      >
        {priceValue}{" "}
        <TextAtm typography="xsmall1">{` ${currency} ${priceTypeText}`}</TextAtm>
      </TextAtm>
    </PushOrg>
  )

  const lowestPriceElement = (
    <PushOrg
      bottomSpace={{
        small: 2,
        medium: 2,
        large: 1,
      }}
      hasForcedFullWidth
      hasNoReset
    >
      <TextAtm typography="nano1" pushSpace={{ right: 0.5 }}>
        {_t("prices.lowerPriceText")} {lowestPrice}
      </TextAtm>
    </PushOrg>
  )

  const priceCellElement = (
    <>
      <BreakpointVisibilityOrg from="small" to="large" hasForcedFullWidth>
        {priceElement}

        {hasPromotion ? (
          <AlignContentOrg horizontalAlign="center">
            <WidthOrg widthSpace={24.5}>{lowestPriceElement}</WidthOrg>
          </AlignContentOrg>
        ) : null}

        <ButtonElement
          buttonHref={buttonHref}
          hasPromotion={hasPromotion}
          productData={productData}
          promotedHorizontalProductBox={promotedHorizontalProductBox}
          isExpanded
        />
      </BreakpointVisibilityOrg>

      <BreakpointVisibilityOrg from="large">
        <FlexContainerOrg direction="column" alignItems="flex-end">
          {priceElement}

          {hasPromotion ? (
            <AlignContentOrg horizontalAlign="right">
              {lowestPriceElement}
            </AlignContentOrg>
          ) : null}

          <ButtonElement
            buttonHref={buttonHref}
            hasPromotion={hasPromotion}
            promotedHorizontalProductBox={promotedHorizontalProductBox}
            productData={productData}
            isWider
          />
        </FlexContainerOrg>
      </BreakpointVisibilityOrg>
    </>
  )

  const detailedPriceCellElement = !isEmpty(periodInfo) ? (
    <AlignContentOrg horizontalAlign="right">
      <PushOrg bottomSpace={2} hasForcedFullWidth>
        {udsPrice}
      </PushOrg>

      {withoutButton ? null : (
        <AlignContentOrg
          horizontalAlign={{
            small: "center",
            medium: "center",
            large: "right",
          }}
        >
          <ButtonElement
            buttonHref={buttonHref}
            hasPromotion={hasPromotion}
            promotedHorizontalProductBox={promotedHorizontalProductBox}
            productData={productData}
            isWider
          />
        </AlignContentOrg>
      )}
    </AlignContentOrg>
  ) : (
    <></>
  )

  if (withoutButton && !showDetailedPrices) {
    return (
      <>
        {priceElement}

        {hasPromotion ? (
          <AlignContentOrg horizontalAlign="center">
            {lowestPriceElement}
          </AlignContentOrg>
        ) : null}
      </>
    )
  }

  return showDetailedPrices ? detailedPriceCellElement : priceCellElement
}
